"use client";

import { useState } from "react";
import Collapsible from "@/app/(components)/generics/Collapsible/Collapsible";
import IconFilter from "@/app/(components)/generics/Icons/IconFilter";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProspectActionTypeItem } from "@/app/types/prospect/ProspectAction";
import {
  isSameProspectActionFilter,
  PROSPECT_ACTION_FILTER_ALL,
  ProspectActionFilter,
  ProspectActionFilterMode,
} from "@/app/types/prospect/ProspectActionFilter";
import { ProspectListTab } from "@/app/types/prospect/ProspectListTab";
import { UStyles } from "@/app/utils/UStyles";
import { prospectActionKindOptions } from "../ProspectActions/ProspectActions.logic";
import styles from "./ProspectFilterMenu.module.css";
import { prospectTabOptions } from "./ProspectList.logic";

type Props = {
  tab: ProspectListTab;
  onTabChange: (tab: ProspectListTab) => void;
  actionTypes: ProspectActionTypeItem[];
  actionFilter: ProspectActionFilter;
  onActionFilterChange?: (filter: ProspectActionFilter) => void;
};

export default function ProspectFilterMenu(props: Props) {
  const T = useT();
  const { tab, onTabChange, actionTypes, actionFilter, onActionFilterChange } = props;

  const [isOpen, setIsOpen] = useState(false);

  const isStatusFiltered = tab !== ProspectListTab.ALL;
  const isActionFiltered = actionFilter.mode !== ProspectActionFilterMode.ALL;

  const selectTab = (value: ProspectListTab) => {
    onTabChange(value);
    setIsOpen(false);
  };

  const selectAction = (value: ProspectActionFilter) => {
    onActionFilterChange?.(value);
    setIsOpen(false);
  };

  const sectionTitle = (label: string, isFiltered: boolean) => (
    <span className={styles.sectionTitle}>
      {label}
      {isFiltered && <span className={styles.sectionDot} />}
    </span>
  );

  return (
    <Popover
      isVisible={isOpen}
      setIsVisible={setIsOpen}
      openAfterClick={true}
      closeWhenClickOutside={true}
      isPortaled={true}
    >
      <PopoverTrigger>
        <span
          className={UStyles.classNames(
            styles.trigger,
            (isStatusFiltered || isActionFiltered) && styles.triggerActive,
          )}
        >
          <IconFilter />
        </span>
      </PopoverTrigger>

      <PopoverContent position={{ default: { top: "6px", right: "0" } }}>
        <div className={styles.panel}>
          <Collapsible
            isOpen={isStatusFiltered}
            containerClassName={styles.section}
            trigger={sectionTitle(String(T(TKey.PROSPECT_SEARCH_FILTER_STATUS)), isStatusFiltered)}
            content={
              <div className={styles.list}>
                {prospectTabOptions(T).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={UStyles.classNames(styles.item, value === tab && styles.itemActive)}
                    onClick={() => selectTab(value as ProspectListTab)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          />

          {onActionFilterChange && (
            <Collapsible
              isOpen={isActionFiltered}
              containerClassName={styles.section}
              trigger={sectionTitle(
                String(T(TKey.PROSPECT_SEARCH_FILTER_ACTION)),
                isActionFiltered,
              )}
              content={
                <div className={styles.list}>
                  <button
                    type="button"
                    className={UStyles.classNames(
                      styles.item,
                      isSameProspectActionFilter(PROSPECT_ACTION_FILTER_ALL, actionFilter) &&
                        styles.itemActive,
                    )}
                    onClick={() => selectAction(PROSPECT_ACTION_FILTER_ALL)}
                  >
                    {T(TKey.PROSPECT_ACTION_FILTER_ALL)}
                  </button>

                  <button
                    type="button"
                    className={UStyles.classNames(
                      styles.item,
                      actionFilter.mode === ProspectActionFilterMode.NONE && styles.itemActive,
                    )}
                    onClick={() => selectAction({ mode: ProspectActionFilterMode.NONE })}
                  >
                    {T(TKey.PROSPECT_ACTION_FILTER_NONE)}
                  </button>

                  {prospectActionKindOptions(T).map((option) => (
                    <button
                      key={option.kind}
                      type="button"
                      className={UStyles.classNames(
                        styles.item,
                        actionFilter.kind === option.kind && styles.itemActive,
                      )}
                      onClick={() =>
                        selectAction({ mode: ProspectActionFilterMode.KIND, kind: option.kind })
                      }
                    >
                      {option.label}
                    </button>
                  ))}

                  {actionTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      className={UStyles.classNames(
                        styles.item,
                        actionFilter.typeId === type.id && styles.itemActive,
                      )}
                      onClick={() =>
                        selectAction({ mode: ProspectActionFilterMode.TYPE, typeId: type.id })
                      }
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              }
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
